// apps/accounts/src/pages/partner/PartnerDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbaseClient';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  RefreshCw,
  ArrowRight,
  CreditCard,
  Home,
  Building2,
  Users,
  Calendar,
  Clock,
  Loader2,
  BarChart3,
  PieChart,
  Eye,
  Sparkles,
  Trophy,
  IndianRupee,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RePieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { toast } from 'sonner';

// --- Currency helper � returns only the formatted number ------
const formatNumber = (amount) => {
  if (!amount && amount !== 0) return '0';
  return Number(amount).toLocaleString('en-IN');
};

// --- Small component to display amount with ? icon ------------
const Currency = ({ amount, className = '' }) => (
  <span className={`inline-flex items-center gap-0.5 ${className}`}>
    <IndianRupee className="w-4 h-4 inline-block" />
    {formatNumber(amount)}
  </span>
);

const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#f97316'];

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    sites: 0,
    activeSites: 0,
    collectedAmount: 0,
    projectCost: 0,
    balance: 0,
    netInvestedCapital: 0,
    profitLoss: 0,
    profitMargin: 0,
  });
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [expenseBreakdown, setExpenseBreakdown] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [topSites, setTopSites] = useState([]);
  const [siteNamesMap, setSiteNamesMap] = useState({});

  // --- Fetch Dashboard Data --------------------------------------
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const partners = await pb.collection('partners').getFullList({
        filter: `createdBy = "${user.id}" || email = "${user.email}"`,
      });
      if (!partners.length) {
        setStats({ sites: 0, activeSites: 0, collectedAmount: 0, projectCost: 0, balance: 0, netInvestedCapital: 0, profitLoss: 0, profitMargin: 0 });
        setRevenueTrend([]);
        setExpenseBreakdown([]);
        setRecentTransactions([]);
        setTopSites([]);
        setLoading(false);
        return;
      }
      const partner = partners[0];

      const assignments = await pb.collection('partner_site_assignments').getFullList({
        filter: `partner = "${partner.id}" && isActive = true`,
        expand: 'site',
      });
      const sites = assignments.map(a => a.expand?.site).filter(Boolean);
      const siteIds = sites.map(s => s.id);

      if (!siteIds.length) {
        setStats({ sites: 0, activeSites: 0, collectedAmount: 0, projectCost: 0, balance: 0, netInvestedCapital: 0, profitLoss: 0, profitMargin: 0 });
        setRevenueTrend([]);
        setExpenseBreakdown([]);
        setRecentTransactions([]);
        setTopSites([]);
        setLoading(false);
        return;
      }

      const [expenses, vendorPayments, clientPayments, investments] = await Promise.all([
        pb.collection('project_expenses').getFullList({
          filter: `project ~ "${siteIds.join('||')}"`,
          sort: '-date',
        }),
        pb.collection('vendor_payments').getFullList({
          filter: `project ~ "${siteIds.join('||')}"`,
          sort: '-paymentDate',
          expand: 'project',
        }),
        pb.collection('client_payments').getFullList({
          filter: `project ~ "${siteIds.join('||')}"`,
          sort: '-paymentDate',
          expand: 'project',
        }),
        pb.collection('investments').getFullList({
          filter: `project ~ "${siteIds.join('||')}"`,
          sort: '-date',
        }),
      ]);

      const siteMap = {};
      sites.forEach(s => { siteMap[s.id] = s.name || 'Unnamed Site'; });
      setSiteNamesMap(siteMap);

      const totalSites = sites.length;
      const activeSites = sites.filter(s => s.status === 'ongoing' || s.status === 'active').length;

      const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      const totalVendorPayments = vendorPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const totalClientPayments = clientPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const totalDeposits = investments.filter(i => i.type === 'deposit').reduce((sum, i) => sum + (i.amount || 0), 0);
      const totalWithdrawals = investments.filter(i => i.type === 'withdrawal').reduce((sum, i) => sum + (i.amount || 0), 0);

      const netInvestedCapital = totalDeposits - totalWithdrawals;
      const collectedAmount = totalClientPayments + netInvestedCapital;
      const projectCost = totalExpenses + totalVendorPayments;
      const balance = collectedAmount - projectCost;
      const profitLoss = balance - netInvestedCapital;
      const profitMargin = totalClientPayments > 0 ? ((profitLoss / totalClientPayments) * 100).toFixed(1) : 0;

      setStats({
        sites: totalSites,
        activeSites,
        collectedAmount,
        projectCost,
        balance,
        netInvestedCapital,
        profitLoss,
        profitMargin,
      });

      // --- Revenue Trend (last 30 days) ---
      const last30Days = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayRevenue = clientPayments
          .filter(p => p.paymentDate?.startsWith(dateStr))
          .reduce((sum, p) => sum + (p.amount || 0), 0);
        const dayDeposits = investments
          .filter(inv => inv.type === 'deposit' && inv.date?.startsWith(dateStr))
          .reduce((sum, inv) => sum + (inv.amount || 0), 0);
        const dayWithdrawals = investments
          .filter(inv => inv.type === 'withdrawal' && inv.date?.startsWith(dateStr))
          .reduce((sum, inv) => sum + (inv.amount || 0), 0);
        const dayExpenses = expenses
          .filter(e => e.date?.startsWith(dateStr))
          .reduce((sum, e) => sum + (e.amount || 0), 0);
        const dayVendorPayments = vendorPayments
          .filter(v => v.paymentDate?.startsWith(dateStr))
          .reduce((sum, v) => sum + (v.amount || 0), 0);
        last30Days.push({
          date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
          revenue: dayRevenue + dayDeposits,
          expenses: dayExpenses + dayVendorPayments + dayWithdrawals,
        });
      }
      setRevenueTrend(last30Days);

      // --- Expense Breakdown ---
      const categoryMap = {};
      expenses.forEach(e => {
        const cat = e.category || 'Other';
        categoryMap[cat] = (categoryMap[cat] || 0) + (e.amount || 0);
      });
      const vendorTotal = vendorPayments.reduce((sum, v) => sum + (v.amount || 0), 0);
      if (vendorTotal > 0) {
        categoryMap['Vendor Payments'] = (categoryMap['Vendor Payments'] || 0) + vendorTotal;
      }
      const breakdown = Object.entries(categoryMap).map(([name, value]) => ({
        name,
        value,
      }));
      setExpenseBreakdown(breakdown);

      // --- Recent Transactions ---
      const allTx = [
        ...clientPayments.map(p => ({
          id: p.id,
          label: p.expand?.project?.name || siteMap[p.project] || 'Client Payment',
          type: 'payment',
          amount: p.amount || 0,
          date: p.paymentDate || p.created,
        })),
        ...investments.map(inv => ({
          id: inv.id,
          label: inv.type === 'deposit' ? 'Investment Deposit' : 'Investment Withdrawal',
          type: inv.type,
          amount: inv.type === 'deposit' ? inv.amount : -inv.amount,
          date: inv.date || inv.created,
        })),
      ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10);
      setRecentTransactions(allTx);

      // --- Top Sites by Revenue ---
      const siteRevenue = {};
      clientPayments.forEach(p => {
        const siteId = p.project || p.projectId;
        if (siteId && siteIds.includes(siteId)) {
          siteRevenue[siteId] = (siteRevenue[siteId] || 0) + (p.amount || 0);
        }
      });
      const topSitesData = Object.entries(siteRevenue)
        .map(([id, revenue]) => ({
          id,
          name: siteMap[id] || 'Unknown Site',
          revenue,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
      setTopSites(topSitesData);

    } catch (error) {
      console.error('Error fetching partner dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="w-12 h-12 animate-spin text-amber-500" />
        <p className="text-slate-500 font-medium">Loading partner dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 bg-slate-50/50 min-h-screen">
      {/* --- Header ---------------------------------------------- */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
            <Home className="w-6 h-6 text-amber-500" />
            Partner Dashboard
          </h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-amber-400" />
            Overview of your assigned sites
            <Badge variant="outline" className="text-xs ml-2">?? Read-only</Badge>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDashboardData}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* --- Stats Cards ------------------------------------------ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg shadow-indigo-500/10 bg-gradient-to-br from-indigo-50 to-white hover:shadow-xl transition-all hover:-translate-y-0.5 duration-200">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-indigo-600">My Sites</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{stats.sites}</p>
                <div className="flex items-center gap-1 mt-2 text-xs text-indigo-500">
                  <Building2 className="w-3 h-3" />
                  <span>{stats.activeSites} active</span>
                </div>
              </div>
              <div className="bg-indigo-100 p-3 rounded-2xl">
                <Building2 className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg shadow-emerald-500/10 bg-gradient-to-br from-emerald-50 to-white hover:shadow-xl transition-all hover:-translate-y-0.5 duration-200">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-emerald-600">Collected Amount</p>
                <p className="text-xl font-bold text-slate-800 mt-1">
                  <Currency amount={stats.collectedAmount} />
                </p>
                <div className="flex items-center gap-1 mt-2 text-xs text-emerald-500">
                  <DollarSign className="w-3 h-3" />
                  <span>Revenue + Net Invested Capital</span>
                </div>
              </div>
              <div className="bg-emerald-100 p-3 rounded-2xl">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg shadow-rose-500/10 bg-gradient-to-br from-rose-50 to-white hover:shadow-xl transition-all hover:-translate-y-0.5 duration-200">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-rose-600">Project Cost</p>
                <p className="text-xl font-bold text-slate-800 mt-1">
                  <Currency amount={stats.projectCost} />
                </p>
                <div className="flex items-center gap-1 mt-2 text-xs text-rose-500">
                  <CreditCard className="w-3 h-3" />
                  <span>Expenses + Vendor Payments</span>
                </div>
              </div>
              <div className="bg-rose-100 p-3 rounded-2xl">
                <TrendingDown className="w-6 h-6 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-0 shadow-lg ${stats.profitLoss >= 0 ? 'shadow-blue-500/10 bg-gradient-to-br from-blue-50 to-white' : 'shadow-red-500/10 bg-gradient-to-br from-red-50 to-white'} hover:shadow-xl transition-all hover:-translate-y-0.5 duration-200`}>
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className={`text-sm font-medium ${stats.profitLoss >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  Profit / Loss
                </p>
                <p className={`text-xl font-bold mt-1 ${stats.profitLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {stats.profitLoss >= 0 ? '+' : ''}<Currency amount={stats.profitLoss} />
                </p>
                <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                  <span>{stats.profitLoss >= 0 ? '?? Profitable' : '?? Loss'}</span>
                  <span className="ml-1 text-slate-400">� {stats.profitMargin}% margin</span>
                </div>
              </div>
              <div className={`p-3 rounded-2xl ${stats.profitLoss >= 0 ? 'bg-blue-100' : 'bg-red-100'}`}>
                {stats.profitLoss >= 0 ? (
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* --- Charts ---------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 shadow-sm border-0 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-400" />
                Revenue vs Cost (Last 30 days)
              </CardTitle>
              <Badge variant="outline" className="text-xs">Trend</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              {revenueTrend && revenueTrend.length > 0 && revenueTrend.some(d => d.revenue > 0 || d.expenses > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueTrend}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `?${v}`} />
                    <Tooltip formatter={(value) => <Currency amount={value} />} />
                    <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#revGrad)" />
                    <Area type="monotone" dataKey="expenses" stroke="#f59e0b" strokeWidth={2} fill="url(#expGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <p>No trend data available for your sites</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-0 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-amber-400" />
              Expense Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72 flex items-center justify-center">
              {expenseBreakdown && expenseBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <Pie
                      data={expenseBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {expenseBreakdown.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => <Currency amount={value} />} />
                    <Legend verticalAlign="bottom" height={36} />
                  </RePieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-400 text-sm">No expense data</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* --- Top Sites & Recent Transactions ---------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 shadow-sm border-0 hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Top Sites by Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topSites && topSites.length > 0 ? (
              <div className="space-y-3">
                {topSites.map((s, idx) => (
                  <div
                    key={s.id || idx}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition cursor-pointer group"
                    onClick={() => navigate(`/partner/projects/${s.id}`)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-sm font-bold ${idx === 0 ? 'text-amber-500' : 'text-slate-400'}`}>
                        #{idx + 1}
                      </span>
                      <span className="font-medium text-slate-700 truncate">{s.name || 'Site'}</span>
                    </div>
                    <span className="font-semibold text-emerald-600 text-sm whitespace-nowrap">
                      <Currency amount={s.revenue} />
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400 text-sm">No revenue yet</div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 shadow-sm border-0 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Recent Transactions
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="text-amber-600 hover:text-amber-700"
                onClick={() => navigate('/partner/client-payments')}
              >
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentTransactions && recentTransactions.length > 0 ? (
              <div className="space-y-2">
                {recentTransactions.slice(0, 6).map((tx, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition rounded-lg"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`p-2 rounded-full ${tx.amount >= 0 ? 'bg-emerald-100' : 'bg-rose-100'} flex-shrink-0`}>
                        {tx.type === 'withdrawal' ? (
                          <TrendingDown className="w-4 h-4 text-rose-600" />
                        ) : tx.type === 'deposit' ? (
                          <DollarSign className="w-4 h-4 text-blue-600" />
                        ) : (
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{tx.label || 'Transaction'}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(tx.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <span className={`font-semibold text-sm whitespace-nowrap ${tx.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {tx.amount >= 0 ? '+' : ''}<Currency amount={Math.abs(tx.amount)} />
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400 text-sm">No recent transactions</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* --- Quick Actions ---------------------------------------- */}
      <Card className="shadow-sm border-0 hover:shadow-lg transition-shadow">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate('/partner/projects')}
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2 shadow-md hover:shadow-lg transition-all"
            >
              <Eye className="w-4 h-4" /> View My Sites
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/partner/attendance')}
              className="gap-2 border-slate-200 hover:border-slate-300"
            >
              <Users className="w-4 h-4" /> Labour Attendance
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/partner/expenses')}
              className="gap-2 border-slate-200 hover:border-slate-300"
            >
              <CreditCard className="w-4 h-4" /> Project Expenses
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}